import { useParams, Link } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";
import { HeaderNavigation } from "@/components/HeaderNavigation";
import { FooterSEO } from "@/components/FooterSEO";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { QuickAnswerBox } from "@/components/QuickAnswerBox";
import { GEO_QUESTIONS } from "@/data/geoQuestions";
import { MessageSquare, ArrowRight, Check, ShieldAlert, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import NotFound from "./NotFound";

export default function CornerAnswersPage() {
  const { slug } = useParams<{ slug?: string }>();

  // If slug is present, show single answer page; else show Q&A index
  if (slug) {
    const item = GEO_QUESTIONS.find((q) => q.slug === slug);
    if (!item) return <><SEOHead title="Yanıt bulunamadı" noindex /><NotFound /></>;

    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": item.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": item.quickAnswer
          }
        },
        ...item.faqs.map((f) => ({
          "@type": "Question",
          "name": f.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": f.answer
          }
        }))
      ]
    };

    return (
      <div className="min-h-screen bg-background flex flex-col justify-between">
        <SEOHead
          title={`${item.question} | Corner Cevaplıyor`}
          description={item.quickAnswer}
          canonicalPath={`/corner-cevapliyor/${item.slug}`}
          schemaJson={faqSchema}
        />
        <HeaderNavigation />

        <main className="flex-1 py-8 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto w-full">
          <Breadcrumbs
            items={[
              { label: "Corner Cevaplıyor", url: "/corner-cevapliyor" },
              { label: item.question }
            ]}
          />

          <article className="space-y-6 my-6">
            <div className="space-y-3">
              <span className="px-3 py-1 rounded-full bg-primary/10 text-primary font-bold text-xs uppercase tracking-wider">
                Corner Cevaplıyor • {item.categoryTitle}
              </span>
              <h1 className="text-3xl sm:text-5xl font-black text-foreground tracking-tight leading-tight">
                {item.question}
              </h1>
            </div>

            <p className="text-sm text-muted-foreground">Hazırlayan: Corner Gayrimenkul · İçerik kontrolü: <time dateTime={item.lastUpdated}>6 Ekim 2026</time></p>

            {/* Quick Answer Box (GEO Snippet extraction) */}
            <QuickAnswerBox question={item.question} answer={item.quickAnswer} sourceText="Corner Gayrimenkul editoryal rehberi; resmî kaynaklar aşağıda" />

            {/* Detailed Analysis Paragraphs */}
            <div className="prose prose-slate max-w-none text-foreground text-base sm:text-lg leading-relaxed space-y-4 py-4 border-b border-border">
              <h2 className="text-2xl font-extrabold text-foreground">Arayışınızı nasıl planlayabilirsiniz?</h2>
              {item.detailedAnalysis.map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>

            {/* Pros & Cons */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 my-6">
              <div className="p-6 rounded-2xl bg-green-500/5 border border-green-500/20 space-y-3">
                <h3 className="font-bold text-lg text-green-700 flex items-center gap-2">
                  <Check className="h-5 w-5" /> Kontrol listeniz
                </h3>
                <ul className="space-y-2 text-sm text-foreground">
                  {item.pros.map((pro, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="text-green-600 font-bold">•</span>
                      <span>{pro}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="p-6 rounded-2xl bg-amber-500/5 border border-amber-500/20 space-y-3">
                <h3 className="font-bold text-lg text-amber-700 flex items-center gap-2">
                  <ShieldAlert className="h-5 w-5" /> Dikkat Edilmesi Gerekenler
                </h3>
                <ul className="space-y-2 text-sm text-foreground">
                  {item.cons.map((con, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="text-amber-600 font-bold">•</span>
                      <span>{con}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <section className="rounded-2xl border border-border bg-muted/30 p-6" aria-label="Rehber kaynakları">
              <h2 className="text-lg font-extrabold mb-3">Resmî kaynaklar ve içerik kapsamı</h2>
              <p className="text-sm leading-relaxed text-muted-foreground mb-4">Bu yanıt, mülk seçimini planlamak için hazırlanmış editoryal bir rehberdir. Hizmet ve gösterge bilgilerini aşağıdaki resmî kaynaklardan inceleyebilirsiniz.</p>
              <div className="flex flex-col gap-3">{item.sources.map((source) => <a key={source.url} href={source.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm text-primary underline underline-offset-2">{source.title}<ExternalLink className="h-3.5 w-3.5 shrink-0" /></a>)}</div>
              <Link to={item.relatedPath} className="inline-flex items-center gap-1 text-sm font-bold text-primary mt-5 hover:underline">İlgili rehberi inceleyin <ArrowRight className="h-4 w-4" /></Link>
            </section>

            {/* FAQs */}
            {item.faqs.length > 0 && (
              <div className="bg-card rounded-2xl p-6 border border-border space-y-4 my-8">
                <h3 className="font-extrabold text-xl text-foreground">İlgili Sık Sorulan Sorular</h3>
                <Accordion type="single" collapsible className="w-full space-y-2">
                  {item.faqs.map((f, i) => (
                    <AccordionItem key={i} value={`faq-${i}`} className="border border-border/60 rounded-xl px-4">
                      <AccordionTrigger className="font-bold text-base text-foreground text-left py-3">
                        {f.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground text-sm pb-3">
                        {f.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            )}

            {/* CTA Box */}
            <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent p-8 rounded-2xl border border-primary/20 flex flex-col sm:flex-row items-center justify-between gap-6">
              <div className="space-y-1 text-center sm:text-left">
                <h3 className="text-xl font-bold text-foreground">Adana Gayrimenkul Danışmanlığı İle İletişime Geçin</h3>
                <p className="text-sm text-muted-foreground">Portföy araştırması ve özel gayrimenkul sorularınız için uzman ekibimiz yanınızda.</p>
              </div>
              <Button asChild size="lg" className="font-bold shrink-0">
                <a href={`https://wa.me/905335487801?text=${encodeURIComponent("Merhaba, gayrimenkul arayışım hakkında bilgi almak istiyorum.")}`} target="_blank" rel="noopener noreferrer">
                  <MessageSquare className="h-4 w-4 mr-2" /> Danışmanla Görüşün
                </a>
              </Button>
            </div>
          </article>
        </main>

        <FooterSEO />
      </div>
    );
  }

  // Q&A Index Page
  return (
    <div className="min-h-screen bg-background flex flex-col justify-between">
      <SEOHead
        title="Corner Cevaplıyor | Adana Gayrimenkul Soru Cevap Rehberi"
        description="Adana'da ev almak, ilçeleri karşılaştırmak ve mülkünü satmak için kaynaklı sorular, anlaşılır yanıtlar ve pratik kontrol listeleri."
        canonicalPath="/corner-cevapliyor"
      />
      <HeaderNavigation />

      <main className="flex-1 py-8 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto w-full">
        <Breadcrumbs items={[{ label: "Corner Cevaplıyor" }]} />

        <div className="mb-12 text-center max-w-3xl mx-auto">
          <span className="px-3 py-1 rounded-full bg-primary/10 text-primary font-bold text-xs uppercase tracking-wider">
            Gayrimenkul soru ve yanıtları
          </span>
          <h1 className="text-3xl sm:text-5xl font-black text-foreground tracking-tight my-4">
            Corner Cevaplıyor
          </h1>
          <p className="text-muted-foreground text-lg leading-relaxed">
            Adana'da mülk arayışınızı ve satışa hazırlığınızı planlamak için kaynaklı yanıtlar ve pratik kontrol listeleri.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-8">
          {GEO_QUESTIONS.map((q) => (
            <Card key={q.id} className="p-6 bg-card border border-border/80 hover:border-primary transition-all shadow-xs flex flex-col justify-between">
              <div>
                <span className="text-xs font-bold text-primary uppercase tracking-wider block mb-2">{q.categoryTitle}</span>
                <h3 className="font-extrabold text-xl text-foreground mb-3 hover:text-primary transition-colors">
                  <Link to={`/corner-cevapliyor/${q.slug}`}>
                    {q.question}
                  </Link>
                </h3>
                <QuickAnswerBox answer={q.quickAnswer} sourceText="Corner Gayrimenkul editoryal rehberi" />
              </div>

              <div className="pt-4 border-t border-border/60 flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Son Güncelleme: {q.lastUpdated}</span>
                <Link to={`/corner-cevapliyor/${q.slug}`} className="text-sm font-bold text-primary hover:underline flex items-center gap-1">
                  Detaylı İncele <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </Card>
          ))}
        </div>
      </main>

      <FooterSEO />
    </div>
  );
}
